import { DefaultOptionType } from 'antd/es/select'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import orderBy from 'lodash/orderBy'

import { doesSingleOptionIsEmpty } from 'Shared/utils/array'
import { utils } from 'Shared/utils/url'
import { deleteAllSearchParams } from 'Shared/utils/url/helpers'

import { searchQueryConfig } from 'Core/constants/filters'
import {
	ConfigItem,
	FilterParams,
	FilterTypes,
	FormatOptionsConfigType,
	FormatOptionsType,
	OptionsSortType,
	OrderValues,
	SearchFilterParams,
	SortDirection,
	TableUrlParams
} from 'Core/interfaces'
import {
	entityToLabelId,
	entityToLabelValueWithId,
	entityToNameInValueLabelValue,
	entityToStringLabelValue,
	valueToLabelValue,
	valueToStringLabelValue
} from 'Core/utils/dataFormat'
import { sortOptions } from 'Core/utils/sorting'

import { GenericParamsI, LocationSearch, StateProvince } from './types'

export const prepareGenericParams = (
	{ pageNumber, pageSize, searchQuery, sortingParams, searchFilters, filters }: GenericParamsI,
	fixQueryParam?: boolean
): TableUrlParams => {
	const params = {} as TableUrlParams
	params.page = pageNumber

	if (pageNumber) {
		params.page = pageNumber
	}

	if (pageSize) {
		params.pageSize = pageSize
	}

	// TODO: make consistent on backend query and searchQuery
	if (searchQuery && !fixQueryParam) {
		params.query = searchQuery
	} else if (searchQuery && fixQueryParam) {
		params.searchQuery = searchQuery
	}

	if (sortingParams) {
		params.sortField = sortingParams.field
		params.sortOrder = sortingParams.order
	}

	if (searchFilters) {
		searchFilters.forEach((filter) => {
			const { apiSearchFieldName, data } = filter
			if (apiSearchFieldName === searchQueryConfig.searchQuery.apiSearchFieldName && data) {
				// TODO: make consistent on backend query and searchQuery
				if (fixQueryParam) {
					params.searchQuery = data
				} else {
					params.query = data as string
				}
			} else {
				params[filter.apiSearchFieldName] = filter.data
			}
		})
	}

	if (isObject(filters)) {
		Object.values(filters).forEach((filter) => {
			const { data, apiFilterFieldName, filterType, emptyValue } = filter

			if (data) {
				const isDataShouldBeFiltered =
					isString(emptyValue) &&
					(isArray(data) || isString(data)) &&
					data.includes(emptyValue)

				if (isDataShouldBeFiltered) {
					return
				}

				if (isArray(data) && filterType !== FilterTypes.MULTI_VALUE) {
					params[apiFilterFieldName] = data[0]
					return
				}

				if (
					filterType === FilterTypes.MULTI_VALUE &&
					isArray(data) &&
					(doesSingleOptionIsEmpty(data) || isEmpty(data))
				) {
					return
				}

				params[apiFilterFieldName] = data
			}
		})
	}
	return params
}

export const setURLParams = ({
	searchFilters,
	filters,
	isDeleteAllSearchParams = true
}: {
	searchFilters: SearchFilterParams[]
	filters: { [key: string]: FilterParams }
	isDeleteAllSearchParams?: boolean
}) => {
	isDeleteAllSearchParams && deleteAllSearchParams()
	if (searchFilters.length > 0) {
		searchFilters.forEach((filter) => {
			const { data, UrlParam, UrlParamLabel, label, suffixData } = filter
			if (data) {
				utils.updateUrlParams(UrlParam, data)
				if (UrlParamLabel && label) {
					utils.updateUrlParams(UrlParamLabel, label)
				}

				if (suffixData?.label) {
					utils.updateUrlParams(suffixData.urlParam, suffixData.label)
				}
			}
		})
	}

	if (isObject(filters)) {
		Object.values(filters).forEach((filter) => {
			const { data, UrlParam } = filter
			if (isArray(data)) {
				data.forEach((value) => {
					if (isEmpty(value)) {
						return
					}
					utils.appendSearchParam(UrlParam, value)
				})
				return
			}

			if (data) {
				utils.updateUrlParams(UrlParam, data)
			}
		})
	}
}

export const getDefaultStoreValue = <T>(): StoreValue<T> => ({
	value: null,
	isLoading: false
})

export const getDefaultStoreArrayValue = <T = Entity>(): StoreArrayValue<T> => ({
	value: [],
	isLoading: false
})

export const getDefaultStoreBooleanValue = (value: boolean = false): StoreBooleanValue => ({
	value,
	isLoading: false
})

export const isLoadingEntities = (entities: { isLoading: boolean }[]): boolean =>
	entities.some(({ isLoading }) => isLoading)

export const formatSearchStringOptions =
	<F extends keyof Config, Config>(fieldName: F) =>
	(options: (string | Entity)[], config: Config): DefaultOptionType[] => {
		if (isEmpty(options)) {
			return []
		}

		const configItem = config[fieldName] as ConfigItem<F>

		return [createFormatOption(configItem, options)]
	}

export const formatSearchOptions =
	<F extends {}, Config, FieldName extends keyof F, OptionResponse>(fields: F) =>
	(options: OptionResponse, config: Config) => {
		if (isEmpty(options)) {
			return []
		}

		const configType = config as FormatOptionsConfigType<F>
		const optionsType = options as FormatOptionsType<F>
		return Object.values(fields)
			.filter((fieldName) => configType[fieldName as FieldName])
			.map((fieldName) => {
				const configItem = configType[fieldName as FieldName]
				const data = optionsType[fieldName as FieldName]
				return createFormatOption(configItem, data)
			})
	}

export const createFormatOption = <SearchResultFields>(
	configItem: ConfigItem<SearchResultFields>,
	data: (Key | Entity)[]
) => {
	const {
		apiFieldName,
		filterType,
		apiSearchFieldName,
		UrlParam,
		UrlParamLabel,
		customOptionFormatter,
		optionsSortType,
		suffixData
	} = configItem

	const options = data.map((item, index) => {
		let option: LabelValue<Key>
		if (customOptionFormatter) {
			option = customOptionFormatter(item)
		} else {
			option = getOptionByCondition<SearchResultFields>(item, configItem)
		}

		return {
			...option,
			key: `${configItem.label}-${option.value}-${index}`,
			value: `${apiFieldName}-${option.value}`,
			props: {
				filterType,
				apiSearchFieldName,
				value: option.value,
				label: option.label,
				UrlParam,
				UrlParamLabel,
				suffixData
			}
		}
	})

	const sortedOptions = sortOptions({
		options,
		sortType: optionsSortType || OptionsSortType.default
	})

	return {
		label: configItem.label,
		key: configItem.label,
		options: sortedOptions
	}
}

export const getOptionByCondition = <T>(
	item: Key | Entity,
	{
		isLabelId,
		isLabelCode,
		isLabelState,
		isTurkishUser,
		isLabelAndId,
		isLabelInValue,
		isLocationIdName
	}: ConfigItem<T>
): LabelValue<Key> => {
	if (isString(item)) {
		return valueToLabelValue(item)
	} else if (isNumber(item)) {
		return valueToStringLabelValue(item)
	} else if (isLabelId) {
		return entityToLabelId(item)
	} else if (isLabelCode) {
		return codeEntityToLabelValue(item as CodeEntity)
	} else if (isLabelState) {
		return formatStateProvinceToLabelValue(item as StateProvince, isTurkishUser)
	} else if (isLabelAndId) {
		return entityToLabelValueWithId(item)
	} else if (isLocationIdName) {
		return formatLocationName(item as LocationSearch)
	} else if (isLabelInValue) {
		return entityToNameInValueLabelValue(item)
	} else {
		return entityToStringLabelValue(item)
	}
}

export const formatStateProvinceToLabelValue = (
	entity: StateProvince,
	isTurkishUser: boolean = false
): LabelValueExtra => {
	let label = entity.name

	if (!isTurkishUser && entity.countryCode !== 'TUR') {
		if (entity.code) {
			label += `, ${entity.code}`
		}

		if (entity.countryCode) {
			label += ` (${entity.countryCode})`
		}
	} else {
		if (entity.region) {
			label += ` (${entity.region})`
		}
	}

	return {
		label,
		value: entity.id.toString(),
		extra: entity.countryCode ?? ''
	}
}

export const codeEntityToLabelValue = (
	item: Pick<CodeEntity, 'code' | 'id'>
): LabelValue<number> => ({
	label: item.code,
	value: item.id
})

export const formatLocationName = (entity: LocationSearch): LabelValue<number> => ({
	label: `${entity.companyLocationId} - ${entity.name}`,
	value: entity.id
})

export function getResortedItemsByName<T extends { name: string }>(
	order: SortDirection | undefined,
	items: T[]
) {
	if (!order) {
		return items
	}
	const correctOrder = order === SortDirection.ASC ? OrderValues.asc : OrderValues.desc
	return orderBy(items, 'name', correctOrder)
}

export const getFilterValue = <T>(data: T[] | T | undefined | null) => {
	if (isNil(data)) {
		return null
	}

	return Array.isArray(data) ? data[0] : data
}

export const formatStateProvince = (state: StateProvince): string => {
	let label = state.name

	if (state.code) {
		label += `, ${state.code}`
	}

	if (state.countryCode) {
		label += ` (${state.countryCode})`
	}

	return label
}

export const formatStateProvinceOption = (state: StateProvince): LabelValue => ({
	label: formatStateProvince(state),
	value: state.id.toString()
})
